//@flow

import type {
  StoreTierInfo,
  Subscription,
  StripeSubscriptionStatus,
  Frequency,
  StoreTier,
} from '../../../entities';
import {commaFormat} from '../../../utils/number';

/* 
Possible values are incomplete, incomplete_expired, trialing, active, past_due, canceled, unpaid, or paused. */
type StripeSubscription = {
  id: string,
  currentPeriodEnd: number,
  currentPeriodStart: number,
  created: number,
  status: StripeSubscriptionStatus,

  items: {
    data: {
      plan: {
        id: string,
        amount: number,
      },
    }[],
  },
  metadata: {
    storeTierId: string,
    frequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'SEMIANNUALLY',
  },
};

export function getTierName(storeTierName: string) {
  switch (storeTierName) {
    case 'TIER_3':
    case 'TIER_31':
    case 'TIER_11':
    case 'TIER_33':
      return 'TiendaDa PRO';
    case 'TIER_309':
    case 'TIER_310':
      return 'TiendaDa Básico';
    case 'TIER_109':
      return 'TiendaDa Básico (Periodo de prueba)';
    case 'TIER_110':
      return 'TiendaDa Básico (Periodo de prueba)';
    case 'TIER_120':
      return 'TiendaDa PRO+ (Periodo de prueba)';
    case 'TIER_320':
      return 'TiendaDa PRO+';
    default:
      return 'TiendaDa';
  }
}

function getStoreTierById(id: string): StoreTier {
  const tierName = `TIER_${id}`;
  const tierLabel = getTierName(tierName);

  return {
    name: tierName,
    label: tierLabel,
    type: 'store-tier',
    id: id,
  };
}

function getFrequencyByMetadata(
  frequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'SEMIANNUALLY'
): Frequency {
  switch (frequency) {
    case 'MONTHLY':
      return {label: 'Mensual', months: 1, name: 'MONTHLY', type: 'frequency'};
    case 'QUARTERLY':
      return {
        label: 'Trimestral',
        months: 3,
        name: 'QUARTERLY',
        type: 'frequency',
      };
    case 'ANNUAL':
      return {label: 'Anual', months: 12, name: 'ANNUAL', type: 'frequency'};
    case 'SEMIANNUALLY':
      return {
        label: 'Semestral',
        months: 6,
        name: 'SEMIANNUALLY',
        type: 'frequency',
      };
    default:
      return {
        label: frequency,
        months: 1,
        name: frequency,
        type: 'frequency',
      };
  }
}

function mapStripeToStoreTierInfo(
  stripeSubscription: StripeSubscription
): StoreTierInfo {
  const metadata = stripeSubscription.metadata;
  const storeTier = getStoreTierById(metadata.storeTierId);
  const frequency = getFrequencyByMetadata(metadata.frequency);
  const stripePlan = stripeSubscription.items.data[0].plan;

  return {
    id: stripePlan.id,
    frequency,
    type: 'store-tier-plan',
    storeTier,
    stripeId: stripeSubscription.id,
  };
}

export function mapStripeToSubscription(
  stripeSubscription: StripeSubscription
): Subscription {
  const storeTierPlan = mapStripeToStoreTierInfo(stripeSubscription);

  const planAmount = stripeSubscription?.items?.data[0]?.plan?.amount / 100;

  return {
    id: stripeSubscription.id,
    endDate: stripeSubscription.currentPeriodEnd * 1000,
    nextPaymentCreationDate: stripeSubscription.currentPeriodEnd * 1000,
    paymentDueDate: stripeSubscription.currentPeriodEnd * 1000,
    startDate: stripeSubscription.currentPeriodStart * 1000,
    state: stripeSubscription.status === 'active' ? 'PAID' : 'PENDING',
    stripeSubscriptionStatus: stripeSubscription.status,
    storeTierPlan,
    subscriptionPrice: commaFormat(planAmount, '$'),
    autoGenerated: false,
    type: 'subscription-group',
  };
}
